import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface WishlistContextValue {
  wishlistItems: string[];
  addToWishlist: (productId: string) => void;
  removeFromWishlist: (productId: string) => void;
  isInWishlist: (productId: string) => boolean;
  toggleWishlist: (productId: string) => void;
  clearWishlist: () => void;
}

const WishlistContext = createContext<WishlistContextValue | undefined>(undefined);

const WISHLIST_STORAGE_KEY = "perlla-wishlist";

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [wishlistItems, setWishlistItems] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // Sync with database when user changes
  useEffect(() => {
    if (user) {
      syncWithDatabase();
    } else {
      // Clear wishlist when user logs out
      setWishlistItems([]);
    }
  }, [user]);

  // Refetch wishlist when window/tab regains focus (replaces realtime sub)
  useEffect(() => {
    if (!user) return;
    const onFocus = () => { syncWithDatabase(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user]);

  useEffect(() => {
    try {
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(wishlistItems));
    } catch {}
  }, [wishlistItems]);

  const syncWithDatabase = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from("wishlist_items")
        .select("product_id")
        .eq("user_id", user.id);
      
      if (error) throw error;
      
      const dbWishlistItems = data?.map(item => item.product_id) || [];
      setWishlistItems(dbWishlistItems);
    } catch (error) {
      console.error("Failed to sync wishlist:", error);
    }
  };

  const addToWishlist = async (productId: string) => {
    if (user) {
      try {
        const { error } = await supabase
          .from("wishlist_items")
          .insert({ user_id: user.id, product_id: productId });
        
        if (error) throw error;
      } catch (error) {
        console.error("Failed to add to wishlist:", error);
        return;
      }
    }
    
    setWishlistItems((prev) => {
      if (!prev.includes(productId)) {
        return [...prev, productId];
      }
      return prev;
    });
  };

  const removeFromWishlist = async (productId: string) => {
    if (user) {
      try {
        const { error } = await supabase
          .from("wishlist_items")
          .delete()
          .eq("user_id", user.id)
          .eq("product_id", productId);
        
        if (error) throw error;
      } catch (error) {
        console.error("Failed to remove from wishlist:", error);
        return;
      }
    }
    
    setWishlistItems((prev) => prev.filter((id) => id !== productId));
  };

  const isInWishlist = (productId: string) => {
    return wishlistItems.includes(productId);
  };

  const toggleWishlist = async (productId: string) => {
    if (isInWishlist(productId)) {
      await removeFromWishlist(productId);
    } else {
      await addToWishlist(productId);
    }
  };

  const clearWishlist = async () => {
    if (user) {
      try {
        const { error } = await supabase
          .from("wishlist_items")
          .delete()
          .eq("user_id", user.id);
        
        if (error) throw error;
      } catch (error) {
        console.error("Failed to clear wishlist:", error);
        return;
      }
    }
    
    setWishlistItems([]);
  };

  const value: WishlistContextValue = {
    wishlistItems,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    toggleWishlist,
    clearWishlist,
  };

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error("useWishlist must be used within WishlistProvider");
  return ctx;
};